import type { QuaternionTuple } from 'three';

import type { BoxWithLabel } from '@/type';
import { eulerToQuaternion } from '@/util/math';

import { type LabelingBox, LabelingBoxBuilder } from './model';
import { pickLabelingBoxOverlayFields } from './overlayFields';

const DEFAULT_QUATERNION: QuaternionTuple = [0, 0, 0, 1];

// 활성 상태의 박스 (현재 scene에 표시 중)
const activePool = new Map<number, LabelingBox>();

// 비활성 상태의 박스 (재사용 대기)
const inactivePool: LabelingBox[] = [];

const normalizeBoxQuaternion = (box: BoxWithLabel): QuaternionTuple => {
  if (box.quaternion) return box.quaternion;
  if (!box.rotation) return DEFAULT_QUATERNION;

  return eulerToQuaternion(box.rotation);
};

/**
 * Pool에서 박스 획득 (없으면 새로 생성)
 */
const acquireBox = (box: BoxWithLabel): LabelingBox | null => {
  // 비활성 pool에서 재사용
  const reusableBox = inactivePool.shift();

  const overlayFields = pickLabelingBoxOverlayFields(box);

  if (reusableBox) {
    return reusableBox.reset({
      color: box.color,
      confidence: box.confidence,
      dimension: box.dimension,
      id: box.id,
      label: box.label,
      position: box.position,
      quaternion: normalizeBoxQuaternion(box),
      trackId: box.trackId,
      workType: box.workType,
      ...overlayFields,
    });
  }

  // 새로 생성
  return new LabelingBoxBuilder()
    .setColor(box.color)
    .setConfidence(box.confidence)
    .setDimension(box.dimension)
    .setId(box.id)
    .setLabel(box.label)
    .setPosition(box.position)
    .setQuaternion(normalizeBoxQuaternion(box))
    .setTrackId(box.trackId)
    .setWorkType(box.workType)
    .setMotionType(overlayFields.motionType)
    .setVelocityX(overlayFields.velocityX)
    .setVelocityY(overlayFields.velocityY)
    .setYaw(overlayFields.yaw)
    .build();
};

/**
 * 모든 리소스 정리 - 메모리 완전 해제
 */
export const disposeAllLabelingBox = () => {
  activePool.forEach(box => box.dispose());
  activePool.clear();

  inactivePool.forEach(box => box.dispose());
  inactivePool.length = 0;
};

/**
 * 특정 인덱스의 박스 조회
 */
export const getLabelingBox = (index: number) => activePool.get(index);

/**
 * 활성 박스 목록 조회
 */
export const getLabelingBoxList = (range: number = activePool.size) =>
  Array.from({ length: range })
    .map((_, index) => activePool.get(index))
    .filter((box): box is LabelingBox => box !== undefined);

/**
 * 활성 pool 크기 조회
 */
export const getPoolSize = () => activePool.size;

/**
 * 특정 인덱스의 박스 비활성화 (pool로 반환)
 */
export const releaseLabelingBox = (index: number) => {
  const box = activePool.get(index);

  if (!box) return;

  activePool.delete(index);
  inactivePool.push(box);
};

/**
 * 현재 활성 박스 전체를 비활성화 (Frame 전환용)
 * dispose 없이 재사용을 위해 보관
 */
export const releaseAllLabelingBox = () => {
  activePool.forEach(box => inactivePool.push(box));
  activePool.clear();
};

/**
 * 박스 생성 또는 재사용
 */
export const setLabelingBox = (box: BoxWithLabel, index: number) => {
  // 이미 해당 인덱스에 박스가 있으면 업데이트
  const existingBox = activePool.get(index);

  if (existingBox) {
    return updateLabelingBox(box, index);
  }

  const labelingBox = acquireBox(box);

  if (!labelingBox) {
    console.error('labelingBox 생성 실패');
    return null;
  }

  activePool.set(index, labelingBox);

  return labelingBox;
};

/**
 * 박스 목록 일괄 설정 (Frame 데이터 로드용)
 */
export const setLabelingBoxList = (boxList: BoxWithLabel[]) => {
  const nextBoxListLength = boxList.length;

  // 새 데이터로 박스 설정
  const result = boxList.map((box, index) => setLabelingBox(box, index));

  // 새 목록보다 큰 index 의 활성 박스를 반환해 잔여 인스턴스가 남지 않게 합니다.
  Array.from(activePool.keys()).forEach(index => {
    if (index < nextBoxListLength) return;

    releaseLabelingBox(index);
  });

  return result;
};

/**
 * 기존 박스 데이터 업데이트
 */
export const updateLabelingBox = (
  box: BoxWithLabel,
  index: number,
): LabelingBox | undefined => {
  const labelingBox = activePool.get(index);

  if (!labelingBox) {
    console.error('labelingBox is null');
    return;
  }

  labelingBox.reset({
    color: box.color,
    confidence: box.confidence,
    dimension: box.dimension,
    id: box.id,
    label: box.label,
    position: box.position,
    quaternion: normalizeBoxQuaternion(box),
    trackId: box.trackId,
    workType: box.workType,
    ...pickLabelingBoxOverlayFields(box),
  });

  return labelingBox;
};
